"use client"
import { useEffect, useState } from "react"
import { Content, isFilled } from "@prismicio/client"
import { SliceComponentProps } from "@prismicio/react"
import Section from "@aura-design/system/section"
import Grid from "@aura-design/system/grid"
import Input from "@aura-design/system/input"
import Select from "@airplan/ui/select"
import Button from "@aura-design/system/button"
import { useForm } from "@aura-design/system/form"
import useComercialRoutes from "@airplan/hooks/routes"
import CollectionRoute from "@airplan/ui/collection-route"
import {
  capitalizeFirstLetterRegex,
  createContainRegexFromString,
} from "@airplan/utils/data-view"
import { ReloadIcon } from "@radix-ui/react-icons"

/**
 * Props for `SectionRoutes`.
 */
export type SectionRoutesProps = SliceComponentProps<Content.SectionRoutesSlice>

/**
 * Component for "SectionRoutes" Slices.
 */
const SectionRoutes = ({ slice, context }: SectionRoutesProps): JSX.Element => {
  const { PROJECT_UID } = context
  const [routesType, setRoutesType] = useState("NATIONAL")
  const [dataFiltered, setDataFiltered] = useState(null)
  const [currentCities, setCurrentCities] = useState([])
  const comercialRoutes = useComercialRoutes({ uid: PROJECT_UID })

  const formData = useForm({
    city: "",
    state: "",
    country: "",
  })
  const routeTypeDocumentResolver = {
    NATIONAL: "Nacional",
    INTERNATIONAL: "Internacional",
  }
  const { city, state, country } = formData

  function getUniqueValues(data) {
    if (!data)
      return {
        states: [],
        cities: [],
        country: [],
      }

    const uniqueStates = new Set()
    const uniqueCities = new Set()
    const uniqueCountries = new Set()

    data.forEach((item) => {
      if (isFilled.integrationField(item.data.airport_destination)) {
        item.data.airport_destination.state &&
          uniqueStates.add(
            capitalizeFirstLetterRegex(item.data.airport_destination.state),
          )
        item.data.airport_destination.city &&
          uniqueCities.add(
            capitalizeFirstLetterRegex(item.data.airport_destination.city),
          )
        item.data.airport_destination.country &&
          uniqueCountries.add(
            capitalizeFirstLetterRegex(item.data.airport_destination.country),
          )
      } else {
        item.data.state &&
          uniqueStates.add(capitalizeFirstLetterRegex(item.data.state))
        item.data.city &&
          uniqueCities.add(capitalizeFirstLetterRegex(item.data.city))
        item.data.country &&
          ["colombia"].includes(item.data.country.toLowerCase()) &&
          uniqueCountries.add(capitalizeFirstLetterRegex(item.data.country))
      }
    })

    return {
      states: Array.from(uniqueStates),
      cities: Array.from(uniqueCities),
      countries: Array.from(uniqueCountries),
    }
  }

  const uniqueCategories = getUniqueValues(comercialRoutes.data?.results)
  const handleOnSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    let filter = comercialRoutes.data?.results || []

    if (routesType === "NATIONAL") {
      filter = state?.value
        ? filter.filter((item) => {
            if (isFilled.integrationField(item.data.airport_destination)) {
              return (
                item.data.airport_destination.state.toLowerCase() ===
                state.value.toLowerCase()
              )
            }
            return item.data.state.toLowerCase() === state.value.toLowerCase()
          })
        : filter.filter((item) => item.data.type === "Nacional")
    } else {
      filter = country.value
        ? filter.filter((item) => {
            if (isFilled.integrationField(item.data.airport_destination)) {
              return (
                item.data.airport_destination.country.toLowerCase() ===
                country.value.toLowerCase()
              )
            }
            return (
              item.data.country.toLowerCase() === country.value.toLowerCase()
            )
          })
        : filter.filter((item) => item.data.type === "Internacional")
    }

    if (city?.value) {
      const regex = createContainRegexFromString(
        city.value.toString().toLocaleLowerCase().trim(),
      )
      filter = filter.filter((item) => {
        if (isFilled.integrationField(item.data.airport_destination)) {
          return regex.test(item.data.airport_destination.city)
        }
        return regex.test(item.data.city)
      })
    }

    setDataFiltered(filter)
  }

  const hasInternational = comercialRoutes.data?.results.some(
    (item) => item.data.type === "Internacional",
  )

  const screenData = dataFiltered
    ? dataFiltered
    : hasInternational
      ? comercialRoutes.data?.results.filter(
          (item) => item.data.type === routeTypeDocumentResolver[routesType],
        )
      : comercialRoutes.data?.results

  useEffect(() => {
    if (!hasInternational) return
    setDataFiltered(null)
    state?.setValue("")
    country?.setValue("")
  }, [routesType])

  useEffect(() => {
    if (country?.value) {
      const uniqueCitiesByCountry = new Set()
      comercialRoutes.data.results
        .filter((item) => item.data.country === country.value)
        .forEach((item) => {
          item.data.city && uniqueCitiesByCountry.add(item.data.city)
        })
      setCurrentCities(Array.from(uniqueCitiesByCountry))
    }
  }, [country?.value])

  const classNameConnectDepartures: string[] = [
    "rounded-br-none rounded-bl-none w-full md:w-[300px] shadow",
  ]
  const classNameConnectArrives: string[] = [
    "rounded-br-none rounded-bl-none w-full md:w-[300px] shadow",
  ]

  if (routesType === "NATIONAL") {
    classNameConnectDepartures.push("bg-dark-blue text-white border-dark-blue")
    classNameConnectArrives.push(
      "bg-white text-dark-blue border-white hover:text-white",
    )
  } else {
    classNameConnectArrives.push("bg-dark-blue text-white border-dark-blue")
    classNameConnectDepartures.push(
      "bg-white text-dark-blue border-white hover:text-white",
    )
  }

  const handleOnReset = () => {
    city?.reset()
    state?.reset()
    country?.reset()

    setDataFiltered(null)
  }

  return (
    <Section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      container="smesh"
      space="aura"
    >
      {hasInternational && (
        <>
          <Button
            className={classNameConnectDepartures.join(" ")}
            onClick={() => setRoutesType("NATIONAL")}
          >
            Nacionales
          </Button>
          <Button
            className={classNameConnectArrives.join(" ")}
            onClick={() => setRoutesType("INTERNATIONAL")}
          >
            Internacionales
          </Button>
        </>
      )}
      <div className="shadow bg-white aura">
        <form onSubmit={handleOnSubmit}>
          <Grid col="three">
            <div>
              <label>Destino</label>
              <Input {...city} list="city" />
              <datalist id="city">
                {uniqueCategories.cities.sort().map((item) => {
                  return <option key={item} value={item}></option>
                })}
              </datalist>
            </div>
            {routesType === "NATIONAL" && (
              <div>
                <label>Departamento</label>
                <Select placeholder="Selecciona una opción" {...state}>
                  {uniqueCategories.states.sort().map((item) => {
                    return (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    )
                  })}
                </Select>
              </div>
            )}
            {routesType === "INTERNATIONAL" && (
              <div className="span-1">
                <label>País</label>
                <Select placeholder="Selecciona una opción" {...country}>
                  {uniqueCategories.countries.sort().map((item) => {
                    return <option key={item}>{item}</option>
                  })}
                </Select>
              </div>
            )}
            <div>
              <Button label="Buscar" className="mt-1.5" isFluid />
            </div>
          </Grid>
        </form>
      </div>

      {(city?.value || state?.value || country?.value) && (
        <div className="text-right">
          <Button mode="menu" onClick={handleOnReset}>
            <ReloadIcon className="icon mr-1" />
            Restablecer
          </Button>
        </div>
      )}
      <div className="text-center text-dark-blue mt-2">
        <div className="mt-2">
          {comercialRoutes.isLoading ? (
            "Cargando..."
          ) : comercialRoutes.isError ? (
            "Ha occurido un error."
          ) : comercialRoutes.data.results.length === 0 ? (
            "Ruta no disponible"
          ) : (
            <Grid col="three">
              {screenData
                .sort((a, b) => {
                  const cityA = isFilled.integrationField(
                    a.data.airport_destination,
                  )
                    ? a.data.airport_destination.city
                    : isFilled.select(a.data.city)
                      ? a.data.city
                      : ""
                  const cityB = isFilled.integrationField(
                    b.data.airport_destination,
                  )
                    ? b.data.airport_destination.city
                    : isFilled.select(b.data.city)
                      ? b.data.city
                      : ""
                  return cityA.localeCompare(cityB)
                })
                .map((item) => (
                  <CollectionRoute doc={item} key={item.id} />
                ))}
            </Grid>
          )}
        </div>
      </div>
    </Section>
  )
}

export default SectionRoutes
